import { Injector } from '@angular/core';
import { LuceneExpression, toString as luceneToString } from 'lucene';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import moment from 'moment';
import { FilterModel, GridApi, IServerSideGetRowsParams } from '@ag-grid-community/core';

import { formatTextDimension } from '../../../utils/number.format';
import { DatacenterSelectionService } from '../../../services/datacenter-selection.service';
import { OrderBy, OrderByDirection } from '../../../utils/constant';
import { Batch } from '../../../../order/models/batch';
import { BatchesService } from '../../../../order/services/batches.service';
import { PaginatedResult } from '../../../models/paginated-result';
import { ColDefWithFields, ColumnType } from '../../column-type';
import { CommonGridOptions } from '../../grid-options';
import { FilterDateFormatType, DATE_FORMAT_PATTERN_DD_MM_YYYY,
	WEEKYEAR_FORMAT_PATTERN } from '../../../models/filter-formats';
import { Strategy } from '../strategy';
import { FilterModel as FilterModelValue, FilterType } from '../../../models/ag-grid';
import { DatacenterType, UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { DatacenterFilterUtilsService } from '../../../services/datacenter-filter-utils.service';
import { TranslationService } from '../../../services/translation.service';
import {
	createDimensionFilter, createReferenceItemFilter, createNumberFilter,
	createDateFilter, createDropDownFilter, createTextFilter,
	createProducedDimensionFilter
} from '../../../services/datacenter-filter';

export class StrategyBatches implements Strategy<Batch> {
	translationService: TranslationService;
	batchesService: BatchesService;
	selectionService: DatacenterSelectionService;
	defaultOrderBy = new OrderBy<keyof Batch>('batchReference', OrderByDirection.DESC);
	hasTotal = true;
	filterService: DatacenterFilterUtilsService;

	aggregateFields = ['qtyBatchGross', 'qtyBatchNet'];

	constructor(injector: Injector) {
		this.translationService = injector.get<TranslationService>(TranslationService);
		this.batchesService = injector.get<BatchesService>(BatchesService);
		this.selectionService = injector.get<DatacenterSelectionService>(DatacenterSelectionService);
		this.filterService = injector.get<DatacenterFilterUtilsService>(DatacenterFilterUtilsService);
	}

	async getAllDataSelected(
		currentColumns: string[],
		filter: string | undefined,
		orderBy: OrderBy<keyof Batch>[],
	) {
		return this.getAllData(orderBy, currentColumns, filter)
			.then((batches: PaginatedResult<Batch>) => this.removeDeselectedRows(batches));
	}

	async getAllData(
		orderBy: OrderBy<keyof Batch>[],
		currentColumns: string[],
		filter: string | undefined
	) {
		return firstValueFrom(this.batchesService.getDatacenterBatchesV5(
			<(keyof Batch)[]> currentColumns,
			filter,
			0,
			60_000,
			orderBy,
			false
		));
	}

	formatCellByType(columnDef: ColDefWithFields<Batch>, data: Batch) {
		const field = columnDef.field || '';
		const fields = columnDef.fields || [];
		const { columnType } = columnDef;
		switch (columnType) {
		case ColumnType.DIMENSION_COLUMN:
			return formatTextDimension(
				<string> data[<keyof Batch> fields[0]],
				<string> data[<keyof Batch> fields[1]],
				<string> data[<keyof Batch> fields[2]]
			);
		case ColumnType.ORDER_NUMBER_COLUMN:
		{
			let orderNumber = '';
			if (<string> data[<keyof Batch> fields[0]]) {
				orderNumber = <string> data[<keyof Batch> fields[0]];
			}
			if (<string> data[<keyof Batch> fields[0]] && <string> data[<keyof Batch> fields[1]]) {
				orderNumber += `/${ <string> data[<keyof Batch> fields[1]] }`;
			}
			return orderNumber;
		}
		case ColumnType.NUMBER_COLUMN:
		{
			const value = <number | undefined >data[<keyof Batch> field];
			if (field === 'producedLength' && value !== undefined) {
				return value.toFixed(2);
			}
			return value === undefined ? '' : String(value);
		}
		case ColumnType.TEXT_COLUMN:
			if (field === 'ecallStatus') {
				const status = <string | undefined>data[<keyof Batch>field];
				if (status === 'O') {
					return this.translationService.getTR('ecall-off.open-or-callable');
				}
				if (status === 'C') {
					return this.translationService.getTR('ecall-off.closed-or-called');
				}
				return '';
			}
			return field === 'steelgradeDescription' ? data[field] : <string> data[<keyof Batch> field];
		case ColumnType.TRANSLATED_COLUMN:
			if (field === 'supplierOrderType') {
				return <string> data[<keyof Batch> field];
			}
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			return <string>data[<keyof Batch>columnDef.filterParams.batchFields[1]];
		case ColumnType.DATE_COLUMN:
			if (data[<keyof Batch> field]) {
				const dateformat = (<FilterDateFormatType> columnDef.cellRendererParams).outputFormat === 'week'
					? WEEKYEAR_FORMAT_PATTERN : DATE_FORMAT_PATTERN_DD_MM_YYYY;
				const weekData = dateformat === WEEKYEAR_FORMAT_PATTERN
					// eslint-disable-next-line @typescript-eslint/no-base-to-string
					&& data[<keyof Batch> field]?.toString().includes('1900-W01')
					? '' : data[<keyof Batch> field];
				return weekData === '' ? '' : moment.utc(<string> weekData).format(dateformat);
			}
			return '';
		case ColumnType.COLOUR_COLUMN:
			if (data[<keyof Batch>field]) {
				const value = <string>data[<keyof Batch>field];
				const colorMapping: Record<string, string> = {
					// eslint-disable-next-line @typescript-eslint/naming-convention
					1: 'FF008000', 2: 'FFFFA500', 3: 'FFFF0000',
				};
				return colorMapping[value];
			}
			return 'FFFFFFFF';
		default:
			throw new Error('unknown filter type');
		}
	}

	getCustomGridOptions(defaultGridOptions: CommonGridOptions) {
		return new CommonGridOptions<Batch>({
			...defaultGridOptions,
			getRowId: ({ data }: { data: Batch }) => data.id,
		});
	}

	getDataFromGrid(
		orderBy: OrderBy<keyof Batch>[],
		currentColumns: (keyof Batch)[],
		filter: string | undefined,
		params: IServerSideGetRowsParams<Batch>,
		rowsCountSubject: BehaviorSubject<number | undefined>
	) {
		const currentColumnsWithId = this.addIdColumn(currentColumns);
		return this.batchesService.getDatacenterBatchesV5(
			currentColumnsWithId,
			filter,
			params.request.startRow ?? 0,
			250,
			orderBy,
			false
		).subscribe({
			next: res => {
				const lastRow = params.request.endRow === undefined || res.count <= params.request.endRow
					? res.count
					: -1;
				params.success({ rowData: res.results, rowCount: lastRow });
				rowsCountSubject.next(res.count);
			},
			error: (err: unknown) => {
				console.error('error requesting batch datacenter', err);
				rowsCountSubject.next(undefined);
				params.fail();
			}
		});
	}

	setTotal(api?: GridApi, filter?: string) {
		return this.batchesService.getAggregatedQuantityForBatches(filter)
			.subscribe({
				next: value => {
					api?.setGridOption('pinnedTopRowData', [value]);
				},
				error: (err: unknown) => {
					console.error('error loading', err);
				}
			});
	}

	addIdColumn(currentColumns: (keyof Batch)[]) {
		if (!currentColumns.includes('id')) {
			currentColumns.push('id');
		}
		if (!currentColumns.includes('form')) {
			currentColumns.push('form');
		}
		if (currentColumns.includes('requestedDeliveryDateFrom')) {
			if (!currentColumns.includes('requestedDateForCallOffType')) {
				currentColumns.push('requestedDateForCallOffType');
			}
			if (!currentColumns.includes('rowCreationDate')) {
				currentColumns.push('rowCreationDate');
			}
		}
		return currentColumns;
	}

	createAggregateCustomViewFilter(view: UserDatacenterView) {
		if (this.filterService.getLuceneFromAllFilters(view)) {
			const criterionFilterNode = this.filterService.getLuceneFromAllFilters(view);
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			return luceneToString(criterionFilterNode!);
		}
		return '';
	}

	createAggregateQuickSearchFilter(
		currentSelectedView: UserDatacenterView,
		api?: GridApi,
		filterModel?: FilterModel
	) {
		let criterionFilterNode;
		let filter = '';
		if (!filterModel) {
			return filter;
		}
		for (const [columnId, filterModel1] of Object.entries(<Record<string, FilterModelValue>> filterModel)) {
			const field = <DatacenterType | undefined> api?.getColumnDef(columnId)?.field;
			const colId = <DatacenterType | undefined> api?.getColumnDef(columnId)?.colId;
			const { fields } = <ColDefWithFields> api?.getColumnDef(columnId);
			const { multiFilterCodes } = <ColDefWithFields> api?.getColumnDef(columnId);
			let criterionFilterValueNode;
			switch (filterModel1.customFilterName) {
			case FilterType.DIMENSION_FILTER:
				criterionFilterValueNode = createDimensionFilter(filterModel1);
				break;
			case FilterType.ORDER_NUMBER_FILTER:
				criterionFilterValueNode = createReferenceItemFilter(filterModel1, fields);
				break;
			case FilterType.NUMBER_FILTER:
				criterionFilterValueNode = createNumberFilter(filterModel1, field);
				break;
			case FilterType.DATE_FILTER:
				criterionFilterValueNode = createDateFilter(filterModel1.date, field);
				break;
			case FilterType.PRODUCED_DIMENSION_FILTER:
				criterionFilterValueNode = createProducedDimensionFilter(filterModel1);
				break;
			case FilterType.DROPDOWN_FILTER:
				criterionFilterValueNode = createDropDownFilter(
					filterModel1.text,
					colId,
					multiFilterCodes
				);
				break;
			case FilterType.TEXT_FILTER:
				criterionFilterValueNode = createTextFilter(filterModel1, field);
				break;
			default:
				throw new Error('unknown filter type');
			}
			if (!criterionFilterNode) {
				criterionFilterNode = criterionFilterValueNode;
				continue;
			}
			criterionFilterNode = {
				left: criterionFilterNode,
				operator: 'AND',
				right: criterionFilterValueNode,
				parenthesized: false
			};
		}
		if (this.filterService.getLuceneFromAllFilters(currentSelectedView)) {
			criterionFilterNode = !criterionFilterNode
				? this.filterService.getLuceneFromAllFilters(currentSelectedView)
				: {
					left: criterionFilterNode,
					operator: 'AND',
					right: this.filterService.getLuceneFromAllFilters(currentSelectedView),
					parenthesized: true,
				};
		}
		filter = luceneToString(<LuceneExpression> criterionFilterNode);
		return filter;
	}

	private removeDeselectedRows(batches: PaginatedResult<Batch>) {
		return batches.results.filter(batch => {
			const rowDeselected = this.selectionService.findDeselectedNode(batch.id);
			return !rowDeselected;
		});
	}
}
